import { PadelSetScore } from "@/types";
import { Button, ButtonGroup, Input } from "@heroui/react";
import { useTranslations } from "next-intl";
import React, { useCallback } from "react";

export interface PadelMatchResultFormProps {
  value?: { sets: PadelSetScore[] };
  onChange?: (result: { sets: PadelSetScore[] }) => void;
  disabled?: boolean;
  team1Name?: string;
  team2Name?: string;
  team1Id?: string;
  team2Id?: string;
}

export const defaultPadelSets: PadelSetScore[] = [
  { team1Games: 0, team2Games: 0, tieBreak: undefined },
  { team1Games: 0, team2Games: 0, tieBreak: undefined },
  { team1Games: 0, team2Games: 0, tieBreak: undefined },
];

// Valid padel set score combinations
const VALID_SET_SCORES = [
  [6, 0],
  [6, 1],
  [6, 2],
  [6, 3],
  [6, 4],
  [7, 5],
  [7, 6],
];

// Score button component - defined outside to prevent remounting on each render
const ScoreButton = ({
  score,
  isSelected,
  isWinner,
  onClick,
  isDisabled,
}: {
  score: number;
  isSelected: boolean;
  isWinner: boolean;
  onClick: () => void;
  isDisabled: boolean;
}) => (
  <Button
    size="sm"
    variant={isSelected ? "solid" : "bordered"}
    color={
      isSelected && isWinner ? "success" : isSelected ? "primary" : "default"
    }
    onPress={onClick}
    isDisabled={isDisabled}
    className="min-w-0 w-full h-12 px-0 text-lg font-semibold rounded-lg"
  >
    {score}
  </Button>
);

const PadelMatchResultForm: React.FC<PadelMatchResultFormProps> = ({
  value = { sets: defaultPadelSets },
  onChange,
  disabled = false,
  team1Name,
  team2Name,
  team1Id,
  team2Id,
}) => {
  const t = useTranslations();

  // Check if a score combination is valid for a regular set
  const isValidSetScore = useCallback(
    (team1Score: number, team2Score: number) => {
      return VALID_SET_SCORES.some(
        ([winner, loser]) =>
          (team1Score === winner && team2Score === loser) ||
          (team1Score === loser && team2Score === winner),
      );
    },
    [],
  );

  // Get valid scores for a team given the opponent's score
  const getValidScoresForTeam = useCallback(
    (opponentScore: number): number[] => {
      const validScores: number[] = [];
      for (let score = 0; score <= 7; score++) {
        if (
          VALID_SET_SCORES.some(
            ([winner, loser]) =>
              (score === winner && opponentScore === loser) ||
              (score === loser && opponentScore === winner),
          )
        ) {
          validScores.push(score);
        }
      }
      return validScores;
    },
    [],
  );

  const handleSetChange = useCallback(
    (index: number, team: "team1Games" | "team2Games", val: number) => {
      if (!onChange) return;

      const newSets = value.sets.map((set, i) => {
        if (i === index) {
          const otherTeam = team === "team1Games" ? "team2Games" : "team1Games";

          const updatedSet = { ...set, [team]: val };

          // Regular set - auto-populate opponent based on valid combinations
          const currentOther = set[otherTeam];
          if (val >= 0 && val <= 4) {
            updatedSet[otherTeam] = 6;
          } else if (val === 5) {
            updatedSet[otherTeam] = 7;
          } else if (val === 6) {
            // 6 can pair with 0-4 or 7 (tiebreak)
            if (currentOther >= 0 && currentOther <= 4) {
              updatedSet[otherTeam] = currentOther;
            } else if (currentOther === 7) {
              // Allow 6-7 for tiebreak
              updatedSet[otherTeam] = 7;
            } else {
              updatedSet[otherTeam] = 4;
            }
          } else if (val === 7) {
            // 7 can pair with 5 or 6
            if (currentOther === 5 || currentOther === 6) {
              updatedSet[otherTeam] = currentOther;
            } else {
              updatedSet[otherTeam] = 6;
            }
          }

          return updatedSet;
        }
        return set;
      });
      onChange({ sets: newSets });
    },
    [onChange, value],
  );

  const handleTieBreakChange = useCallback(
    (index: number, team: "team1Points" | "team2Points", val: number) => {
      if (!onChange) return;

      const newSets = value.sets.map((set: PadelSetScore, i: number) => {
        if (i === index) {
          const otherTeam =
            team === "team1Points" ? "team2Points" : "team1Points";
          const otherScore = set.tieBreak?.[otherTeam] ?? 0;

          // Auto-populate logic for tiebreak
          let autoPopulatedOtherScore = otherScore;

          if (val >= 8) {
            autoPopulatedOtherScore = val - 2;
          }

          // Validation
          const higherScore = Math.max(val, autoPopulatedOtherScore);
          const lowerScore = Math.min(val, autoPopulatedOtherScore);

          if (higherScore >= 7) {
            if (lowerScore >= 6) {
              if (higherScore - lowerScore !== 2) {
                return set;
              }
            } else if (lowerScore === 5) {
              if (higherScore !== 7) {
                return set;
              }
            }
          }

          const updatedSet: PadelSetScore = {
            ...set,
            tieBreak: {
              team1Points: set.tieBreak?.team1Points ?? 0,
              team2Points: set.tieBreak?.team2Points ?? 0,
              [team]: val,
              [otherTeam]: autoPopulatedOtherScore,
            },
          };

          // Adjust set score based on tie-break winner
          const newTieBreak = updatedSet.tieBreak!;
          const team1TbPoints = newTieBreak.team1Points;
          const team2TbPoints = newTieBreak.team2Points;

          if (team1TbPoints > 0 || team2TbPoints > 0) {
            const team1WinsTieBreak = team1TbPoints > team2TbPoints;

            if (set.isTieBreakOnly) {
              // Direct tie-break: winner gets 1 game, loser gets 0
              updatedSet.team1Games = team1WinsTieBreak ? 1 : 0;
              updatedSet.team2Games = team1WinsTieBreak ? 0 : 1;
            } else {
              const team1WinsSet =
                updatedSet.team1Games > updatedSet.team2Games;

              if (team1WinsTieBreak !== team1WinsSet) {
                updatedSet.team1Games = [
                  updatedSet.team2Games,
                  (updatedSet.team2Games = updatedSet.team1Games),
                ][0];
              }
            }
          }

          return updatedSet;
        }
        return set;
      });
      onChange({ sets: newSets });
    },
    [onChange, value],
  );

  const handleTieBreakOnlyToggle = useCallback(
    (index: number, checked: boolean) => {
      if (!onChange) return;

      const newSets = value.sets.map((set: PadelSetScore, i: number) => {
        if (i !== index) return set;
        if (checked) {
          return {
            team1Games: 0,
            team2Games: 0,
            tieBreak: { team1Points: 0, team2Points: 0 },
            isTieBreakOnly: true,
          };
        }
        return {
          team1Games: 0,
          team2Games: 0,
          tieBreak: undefined,
          isTieBreakOnly: false,
        };
      });
      onChange({ sets: newSets });
    },
    [onChange, value],
  );

  const showTieBreak = useCallback((set: PadelSetScore) => {
    return (
      (set.team1Games === 6 && set.team2Games === 7) ||
      (set.team1Games === 7 && set.team2Games === 6)
    );
  }, []);

  const isSet3Disabled = useCallback((sets: PadelSetScore[]) => {
    if (!sets || sets.length < 2) return false;

    const getSetWinner = (set: PadelSetScore) => {
      if (set.team1Games > set.team2Games) return "team1";
      if (set.team2Games > set.team1Games) return "team2";
      return null;
    };

    const set1Winner = getSetWinner(sets[0]);
    const set2Winner = getSetWinner(sets[1]);

    return set1Winner !== null && set1Winner === set2Winner;
  }, []);

  const areSetsEven = useCallback((sets: PadelSetScore[]) => {
    if (!sets || sets.length < 2) return false;

    const getSetWinner = (set: PadelSetScore) => {
      if (set.team1Games > set.team2Games) return "team1";
      if (set.team2Games > set.team1Games) return "team2";
      return null;
    };

    const set1Winner = getSetWinner(sets[0]);
    const set2Winner = getSetWinner(sets[1]);

    return (
      set1Winner !== null && set2Winner !== null && set1Winner !== set2Winner
    );
  }, []);

  return (
    <div className="flex flex-col gap-4">
      {value.sets?.map((set: PadelSetScore, i: number) => {
        const isThirdSetDisabled = i === 2 && isSet3Disabled(value.sets);
        const showSet3 = i !== 2 || areSetsEven(value.sets);

        const team1Wins = (set?.team1Games ?? 0) > (set?.team2Games ?? 0);
        const team2Wins = (set?.team2Games ?? 0) > (set?.team1Games ?? 0);

        if (!showSet3) return null;

        const isThirdSet = i === 2;
        const isTieBreakOnly = !!set?.isTieBreakOnly;
        const showRegularTieBreak = !isTieBreakOnly && !!set && showTieBreak(set);
        const showTieBreakInputs = isTieBreakOnly || showRegularTieBreak;

        return (
          <div key={i} className="flex flex-col gap-2">
            {/* Set heading */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-lg font-bold text-primary">
                {t("matches.set")} {i + 1}
              </span>
            </div>
            {isThirdSet && (
              <ButtonGroup size="sm" fullWidth>
                <Button
                  variant={isTieBreakOnly ? "bordered" : "solid"}
                  color={isTieBreakOnly ? "default" : "primary"}
                  isDisabled={disabled || isThirdSetDisabled}
                  onPress={() => handleTieBreakOnlyToggle(i, false)}
                >
                  {t("matches.normalSet")}
                </Button>
                <Button
                  variant={isTieBreakOnly ? "solid" : "bordered"}
                  color={isTieBreakOnly ? "primary" : "default"}
                  isDisabled={disabled || isThirdSetDisabled}
                  onPress={() => handleTieBreakOnlyToggle(i, true)}
                >
                  {t("matches.thirdSetTieBreak")}
                </Button>
              </ButtonGroup>
            )}
            {!isTieBreakOnly && (
              <div className="flex items-start gap-4">
                <div className="flex flex-col gap-3 flex-1">
                  {/* Team 1 */}
                  <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold">{team1Name}</span>
                    <div className="grid grid-cols-8 gap-1">
                      {[0, 1, 2, 3, 4, 5, 6, 7].map((score) => (
                        <ScoreButton
                          key={`t1-${score}`}
                          score={score}
                          isSelected={(set?.team1Games ?? 0) === score}
                          isWinner={team1Wins}
                          onClick={() =>
                            handleSetChange(i, "team1Games", score)
                          }
                          isDisabled={disabled || isThirdSetDisabled}
                        />
                      ))}
                    </div>
                  </div>
                  {/* Team 2 */}
                  <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold">{team2Name}</span>
                    <div className="grid grid-cols-8 gap-1">
                      {[0, 1, 2, 3, 4, 5, 6, 7].map((score) => (
                        <ScoreButton
                          key={`t2-${score}`}
                          score={score}
                          isSelected={(set?.team2Games ?? 0) === score}
                          isWinner={team2Wins}
                          onClick={() =>
                            handleSetChange(i, "team2Games", score)
                          }
                          isDisabled={disabled || isThirdSetDisabled}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Tiebreak inputs */}
            {set && showTieBreakInputs && (
              <div
                className={
                  isTieBreakOnly
                    ? "flex flex-col gap-2"
                    : "flex items-center gap-2 sm:ml-12"
                }
              >
                {!isTieBreakOnly && (
                  <span className="min-w-[60px] text-xs text-default-500">
                    {t("matches.tieBreak")}
                  </span>
                )}
                {isTieBreakOnly ? (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="min-w-[80px] text-sm font-semibold">
                        {team1Name}
                      </span>
                      <Input
                        type="number"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        min={0}
                        max={99}
                        value={(set.tieBreak?.team1Points ?? 0).toString()}
                        isDisabled={disabled || isThirdSetDisabled}
                        onChange={(e) =>
                          handleTieBreakChange(
                            i,
                            "team1Points",
                            Number(e.target.value),
                          )
                        }
                        variant="bordered"
                        size="sm"
                        className="w-20"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="min-w-[80px] text-sm font-semibold">
                        {team2Name}
                      </span>
                      <Input
                        type="number"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        min={0}
                        max={99}
                        value={(set.tieBreak?.team2Points ?? 0).toString()}
                        isDisabled={disabled || isThirdSetDisabled}
                        onChange={(e) =>
                          handleTieBreakChange(
                            i,
                            "team2Points",
                            Number(e.target.value),
                          )
                        }
                        variant="bordered"
                        size="sm"
                        className="w-20"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <Input
                      type="number"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      min={0}
                      max={99}
                      value={(set.tieBreak?.team1Points ?? 0).toString()}
                      isDisabled={disabled || isThirdSetDisabled}
                      onChange={(e) =>
                        handleTieBreakChange(
                          i,
                          "team1Points",
                          Number(e.target.value),
                        )
                      }
                      variant="bordered"
                      size="sm"
                      className="w-16"
                    />
                    <span className="text-lg font-bold">-</span>
                    <Input
                      type="number"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      min={0}
                      max={99}
                      value={(set.tieBreak?.team2Points ?? 0).toString()}
                      isDisabled={disabled || isThirdSetDisabled}
                      onChange={(e) =>
                        handleTieBreakChange(
                          i,
                          "team2Points",
                          Number(e.target.value),
                        )
                      }
                      variant="bordered"
                      size="sm"
                      className="w-16"
                    />
                  </>
                )}
              </div>
            )}
          </div>
        );
      }) ?? null}
    </div>
  );
};

export default PadelMatchResultForm;
